(() => {
  if (window.PerfumiarzProducts?.initialize) {
    window.PerfumiarzProducts.initialize();
    return;
  }

  const ROOT_SELECTOR = '[data-product-grid-view]';
  const PRODUCTS_ROOT_SELECTOR = '[data-perfumiarz-products]';
  const COLLECTION_ROOT_SELECTOR = `${PRODUCTS_ROOT_SELECTOR}[data-collection-url]`;
  const GRID_SELECTOR = '.perfumiarz-products__grid';
  const COLUMNS_OPTION_SELECTOR = '[data-products-columns-option]';
  const COLUMNS_STORAGE_KEY = 'perfumiarz-product-grid-mobile-columns';
  const scheduled = new WeakSet();
  const runAfterPaint = window.requestAnimationFrame?.bind(window) || ((callback) => callback());
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      observer.unobserve(target);
      loadCollectionProducts(target);
    });
  }, { rootMargin: '600px 0px' }) : null;

  const preservePreviewContext = (url) => {
    const currentParams = new URLSearchParams(window.location.search);
    const previewThemeId = currentParams.get('preview_theme_id');

    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
  };

  const getStoredMobileColumns = () => {
    try {
      const storedColumns = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      return storedColumns === '1' || storedColumns === '2' ? storedColumns : null;
    } catch (_error) {
      return null;
    }
  };

  const storeMobileColumns = (columns) => {
    try {
      window.localStorage.setItem(COLUMNS_STORAGE_KEY, columns);
    } catch (_error) {
      // The selected layout still applies for this page if storage is unavailable.
    }
  };

  const applyMobileColumns = (root, columns) => {
    if (!root || (columns !== '1' && columns !== '2')) return;

    root.dataset.mobileColumns = columns;
    root
      .querySelector(`${GRID_SELECTOR}, .collection-catalog__grid, .perfumiarz-favorites__grid`)
      ?.style.removeProperty('min-height');
    root.querySelectorAll(COLUMNS_OPTION_SELECTOR).forEach((button) => {
      const isActive = button.dataset.productsColumnsOption === columns;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  };

  const initializeViewSwitcher = (root) => {
    if (!root) return;

    const initialColumns = getStoredMobileColumns() || root.dataset.defaultMobileColumns || '1';
    applyMobileColumns(root, initialColumns);

    const switcher = root.querySelector('.perfumiarz-products__view-switcher');
    if (!switcher || switcher.dataset.mobileViewInitialized === 'true') return;

    switcher.dataset.mobileViewInitialized = 'true';
    switcher.addEventListener('click', (event) => {
      const button = event.target.closest(COLUMNS_OPTION_SELECTOR);
      if (!button) return;

      const columns = button.dataset.productsColumnsOption;
      if (columns !== '1' && columns !== '2') return;

      storeMobileColumns(columns);
      document.documentElement.dataset.productGridColumns = columns;
      document.querySelectorAll(ROOT_SELECTOR).forEach((productRoot) => applyMobileColumns(productRoot, columns));
    });
  };

  const loadCollectionProducts = async (root) => {
    if (!root || root.dataset.collectionLoading === 'true' || root.dataset.collectionLoaded === 'true') return;

    const sectionId = root.dataset.sectionId;
    const collectionUrl = root.dataset.collectionUrl;
    if (!sectionId || !collectionUrl) return;

    root.dataset.collectionLoading = 'true';
    root.dataset.collectionState = 'loading';
    root.setAttribute('aria-busy', 'true');

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const requestUrl = new URL(collectionUrl, window.location.origin);
      const sortBy = root.dataset.collectionSortBy;

      requestUrl.searchParams.set('section_id', sectionId);
      if (sortBy) requestUrl.searchParams.set('sort_by', sortBy);
      if (root.dataset.availableOnly === 'true') {
        requestUrl.searchParams.set('filter.v.availability', '1');
      }
      preservePreviewContext(requestUrl);

      const response = await fetch(requestUrl.toString(), {
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Section request failed with status ${response.status}`);

      const sectionHtml = await response.text();
      if (!sectionHtml.trim()) throw new Error('Sorted product section was empty');

      const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const renderedSection = parsedDocument.getElementById(`shopify-section-${sectionId}`);
      const renderedGrid = renderedSection?.querySelector(GRID_SELECTOR);
      const currentGrid = root.querySelector(GRID_SELECTOR);
      if (!renderedGrid || !currentGrid) throw new Error('Sorted product grid was not returned');

      // The initial Liquid cards are a fast first paint while the globally
      // sorted cards arrive. Preserve the occupied grid space so swapping the
      // markup cannot move the sections below it.
      const initialGridHeight = Math.ceil(currentGrid.getBoundingClientRect().height);
      if (initialGridHeight > 0) currentGrid.style.minHeight = `${initialGridHeight}px`;
      currentGrid.innerHTML = renderedGrid.innerHTML;
      root.dataset.collectionLoaded = 'true';
      root.dataset.collectionState = 'loaded';
      runAfterPaint(() => currentGrid.style.removeProperty('min-height'));

      const sectionElement = root.closest('.shopify-section');
      if (sectionElement) sectionElement.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    } catch (error) {
      // Initial cards are also our network-error fallback. Only load their
      // images if the correctly sorted section could not replace them.
      root.querySelectorAll('template[data-collection-fallback-image]').forEach((template) => {
        template.replaceWith(template.content.cloneNode(true));
      });
      root.dataset.collectionState = 'error';
      root.dataset.collectionError = error instanceof Error ? error.message : String(error);
      console.error('[Perfumiarz products] Unable to load the globally sorted collection.', error);
    } finally {
      clearTimeout(timeout);
      delete root.dataset.collectionLoading;
      root.removeAttribute('aria-busy');
    }
  };

  const loadAfterHeroPaint = (root) => {
    const hero = document.querySelector('.hero-carousel__image');
    let finished = false;
    let timeout;

    const load = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      hero?.removeEventListener('load', paintThenLoad);
      hero?.removeEventListener('error', paintThenLoad);
      runAfterPaint(() => runAfterPaint(() => loadCollectionProducts(root)));
    };
    const paintThenLoad = () => load();

    root.dataset.collectionState = 'waiting-for-hero';
    root.addEventListener('focusin', load, { once: true });
    timeout = setTimeout(load, 4500);

    if (!hero || hero.complete) load();
    else {
      hero.addEventListener('load', paintThenLoad, { once: true });
      hero.addEventListener('error', paintThenLoad, { once: true });
    }
  };

  const scheduleCollection = (root) => {
    if (scheduled.has(root)) return;
    scheduled.add(root);
    const firstCollection = document.querySelector(COLLECTION_ROOT_SELECTOR);
    if (!observer || window.Shopify?.designMode || !root.hasAttribute('data-defer-collection')) {
      loadCollectionProducts(root);
      return;
    }
    if (root === firstCollection) {
      loadAfterHeroPaint(root);
      return;
    }
    root.dataset.collectionState = 'waiting';
    observer.observe(root);
    root.addEventListener('focusin', () => {
      observer.unobserve(root);
      loadCollectionProducts(root);
    }, { once: true });
  };

  const initialize = (scope = document) => {
    const initializeRoot = (root) => {
      initializeViewSwitcher(root);
      if (root.matches(COLLECTION_ROOT_SELECTOR)) scheduleCollection(root);
    };

    if (scope.matches?.(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  window.PerfumiarzProducts = { initialize };

  initialize();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(COLLECTION_ROOT_SELECTOR).forEach((root) => observer?.unobserve(root));
  });
})();
